// GA4 ecommerce events.
//
// The Google tag itself is installed in public/index.html and handles page
// views on its own. This module adds the funnel on top: which print was
// viewed, what went into the basket, what reached checkout, and what sold.
// Without it the property can say a print page was visited but not whether
// any of the 400-odd prints in the catalogue actually converts.
//
// Everything here is best-effort and must never break a page. The tag can be
// absent for entirely ordinary reasons — an ad blocker, a privacy browser, a
// consent tool, the build-time prerender — so every entry point checks for it
// and every call is wrapped. A missing gtag costs a data point, not a render.

// Prices are quoted in GBP throughout the site (see utility/formatCurrency).
export const CURRENCY = "GBP";

// Purchases are marked here so a refresh of the confirmation page does not
// report a second sale. Namespaced to survive alongside the basket.
const PURCHASE_KEY_PREFIX = "cp:purchase:";

/**
 * Whether an event can be sent right now.
 *
 * navigator.webdriver rules out the prerender pass: scripts/prerender.mjs
 * loads every route in headless Chrome at build time, and while it also blocks
 * the analytics hosts at the network layer, a page should not be trying to
 * report a print view during a build in the first place.
 */
export function isTrackable() {
  if (typeof window === "undefined") return false;
  if (typeof window.gtag !== "function") return false;
  if (typeof navigator !== "undefined" && navigator.webdriver) return false;
  return true;
}

/**
 * Send one event. Never throws.
 *
 * @param {string} name GA4 event name
 * @param {object} params event parameters
 */
export function track(name, params = {}) {
  if (!name || !isTrackable()) return false;
  try {
    window.gtag("event", name, params);
    return true;
  } catch (error) {
    // Analytics is not worth a broken page.
    console.debug("analytics: event failed", name, error);
    return false;
  }
}

// Number(null) and Number("") are both 0, which would turn an absent price or
// an unknown shipping cost into a confident £0.00 in the reports. Absent has
// to stay absent, so these are rejected before the numeric coercion.
function isBlank(value) {
  return value === null || value === undefined || value === "";
}

/** Stripe and the orders table both deal in minor units. */
export function centsToAmount(cents) {
  if (isBlank(cents)) return null;
  const value = Number(cents);
  if (!Number.isFinite(value)) return null;
  return Math.round(value) / 100;
}

/** Round a major-unit figure to the two decimals GA4 reports in. */
function toAmount(value) {
  if (isBlank(value)) return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return Math.round(amount * 100) / 100;
}

/**
 * A basket row, as built by GeneratedPrint's handleAddToBasket, in GA4 shape.
 *
 * item_id is the generation job id rather than the basket row's
 * "<jobId>/<variantId>" composite, so every size and frame of one artwork
 * aggregates to a single product in reporting — which is the question worth
 * answering: does this *print* sell. The variant is kept alongside it.
 */
export function basketItemToGa4Item(item, index) {
  if (!item) return null;
  const [jobIdFromId = ""] = String(item.id || "").split("/");
  const ga4Item = {
    item_id: item.generationJobId || jobIdFromId || item.sku || "unknown",
    item_name: String(item.title || "Create Prints wall art").trim(),
    quantity: Number(item.quantity) || 1,
  };

  const price = toAmount(item.price);
  if (price !== null) ga4Item.price = price;

  // The variant a buyer actually chose — format, size and frame are the three
  // decisions on the print page, and which combination sells is the point.
  const variant = [item.format, item.size, item.frame]
    .filter((part) => part != null && part !== "")
    .join(" / ");
  if (variant) ga4Item.item_variant = variant;

  if (Number.isInteger(index)) ga4Item.index = index;
  return ga4Item;
}

/** A line from the checkout/summary API in GA4 shape. */
export function summaryItemToGa4Item(item, index) {
  if (!item) return null;
  const ga4Item = {
    item_id: item.id || item.name || "unknown",
    item_name: String(item.name || "Create Prints wall art").trim(),
    quantity: Number(item.quantity) || 1,
  };
  const price = centsToAmount(item.amountSubtotalCents ?? item.amountTotalCents);
  if (price !== null) ga4Item.price = price;
  if (Number.isInteger(index)) ga4Item.index = index;
  return ga4Item;
}

/** A row from orders.order_items in GA4 shape. */
export function orderItemToGa4Item(item, index) {
  if (!item) return null;
  const ga4Item = {
    item_id: item.generation_job_id || item.product_id || item.id || "unknown",
    item_name: String(
      item.description || item.product_name || "Create Prints wall art"
    ).trim(),
    quantity: Number(item.quantity) || 1,
  };
  const price = centsToAmount(
    item.charged_product_price_cents ?? item.price ?? item.amount_total
  );
  if (price !== null) ga4Item.price = price;
  if (Number.isInteger(index)) ga4Item.index = index;
  return ga4Item;
}

function sumItemValue(items) {
  return toAmount(
    items.reduce(
      (total, item) => total + (Number(item.price) || 0) * (Number(item.quantity) || 1),
      0
    )
  );
}

export function trackViewItem(item) {
  const ga4Item = basketItemToGa4Item(item);
  if (!ga4Item) return false;
  return track("view_item", {
    currency: CURRENCY,
    value: sumItemValue([ga4Item]),
    items: [ga4Item],
  });
}

export function trackAddToCart(item) {
  const ga4Item = basketItemToGa4Item(item);
  if (!ga4Item) return false;
  return track("add_to_cart", {
    currency: CURRENCY,
    value: sumItemValue([ga4Item]),
    items: [ga4Item],
  });
}

export function trackRemoveFromCart(item) {
  const ga4Item = basketItemToGa4Item(item);
  if (!ga4Item) return false;
  return track("remove_from_cart", {
    currency: CURRENCY,
    value: sumItemValue([ga4Item]),
    items: [ga4Item],
  });
}

export function trackBeginCheckout(basketItems) {
  const items = (basketItems || []).map(basketItemToGa4Item).filter(Boolean);
  if (!items.length) return false;
  return track("begin_checkout", {
    currency: CURRENCY,
    value: sumItemValue(items),
    items,
  });
}

/**
 * Whether this Stripe checkout session has already been reported as a sale.
 *
 * The confirmation page polls, and a buyer can refresh it or return to the URL
 * from an email; GA4 does not deduplicate purchases for us, so a repeat visit
 * would otherwise book the same order again.
 */
export function hasTrackedPurchase(transactionId) {
  if (!transactionId || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PURCHASE_KEY_PREFIX + transactionId) !== null;
  } catch (error) {
    // Storage can be unavailable (private mode, blocked cookies). Reporting a
    // duplicate is the lesser evil against never reporting a sale at all.
    return false;
  }
}

function markPurchaseTracked(transactionId) {
  if (!transactionId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PURCHASE_KEY_PREFIX + transactionId, String(Date.now()));
  } catch (error) {
    /* nothing to do — see hasTrackedPurchase */
  }
}

/**
 * Where a checkout came from, as GA4 event parameters.
 *
 * Agent buyers never touch the site until the payment page — the agent hands
 * over a /checkout?sessionId=… link, so there is no referrer and no landing
 * page, and GA4 files every one of them under "direct". The channel is only
 * knowable server-side, where the checkout was created; the checkout summary
 * endpoint reports it back on `attribution` and this maps it onto the event.
 *
 * @param {{surface?: string, channel?: string, client_name?: string}} attribution
 */
export function attributionParams(attribution) {
  if (!attribution || typeof attribution !== "object") return {};
  const params = {};
  if (attribution.channel) params.checkout_channel = String(attribution.channel);
  if (attribution.surface) params.checkout_surface = String(attribution.surface);
  return params;
}

/**
 * Report a completed sale, at most once per Stripe checkout session.
 *
 * @param {object} purchase
 * @param {string} purchase.transactionId Stripe checkout session id
 * @param {Array}  purchase.items GA4 items, already mapped
 * @param {number} [purchase.valueCents] order total in minor units
 * @param {number} [purchase.shippingCents]
 * @param {number} [purchase.taxCents]
 */
export function trackPurchase({
  transactionId,
  items = [],
  valueCents,
  shippingCents,
  taxCents,
  attribution,
} = {}) {
  if (!transactionId || !items.length) return false;
  if (hasTrackedPurchase(transactionId)) return false;

  const params = {
    transaction_id: transactionId,
    currency: CURRENCY,
    value: centsToAmount(valueCents) ?? sumItemValue(items) ?? 0,
    items,
    ...attributionParams(attribution),
  };
  const shipping = centsToAmount(shippingCents);
  if (shipping !== null) params.shipping = shipping;
  const tax = centsToAmount(taxCents);
  if (tax !== null) params.tax = tax;

  // Mark first: a tag that is present but failing should not leave the page
  // free to re-report the same order on every retry of the polling loop.
  markPurchaseTracked(transactionId);
  // Also as a user property, so the channel can slice a whole session's
  // reports rather than only this one event.
  if (params.checkout_channel && isTrackable()) {
    try {
      window.gtag("set", "user_properties", {
        checkout_channel: params.checkout_channel,
      });
    } catch (error) {
      console.debug("analytics: user property failed", error);
    }
  }
  return track("purchase", params);
}
